import {
  Map,
  List,
  Set,
  fromJS,
} from 'immutable'
import _reduce from 'lodash/reduce'
import _get from 'lodash/get'
import _find from 'lodash/find'
import _toString from 'lodash/toString'
import _toNumber from 'lodash/toNumber'
import {
  SUBSCRIPTION_CODE_EVENTS_PLUS,
  hasLiveAccessEntitlement,
} from 'services/subscription'
import {
  TYPE_TAG_YOGA_STYLE,
  TYPE_TAG_YOGA_LEVEL,
  TYPE_TAG_YOGA_DURATION,
  TYPE_TAG_YOGA_FOCUS,
  TYPE_TAG_FITNESS_STYLE,
  TYPE_TAG_FITNESS_LEVEL,
  TYPE_TAG_FITNESS_DURATION,
  TYPE_TAG_FITNESS_FOCUS,
  TYPE_TAG_MEDITATION_STYLE,
  TYPE_TAG_MEDITATION_LEVEL,
  TYPE_TAG_MEDITATION_DURATION,
  TYPE_TAG_MEDITATION_FOCUS,
  TYPE_TAG_GENRE,
  TYPE_TAG_CENTERS,
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_EPISODE,
  SUBTYPE_CONTENT_YOGA,
  SUBTYPE_CONTENT_MEDITATION,
  SUBTYPE_CONTENT_FITNESS,
  TYPE_DISPLAY_GUIDE,
  SUBTYPE_CONTENT_EVENT,
  SUBTYPE_CONTENT_GUIDE,
  TYPE_CONTENT_VIDEO,
} from 'services/content-type'
import { NODE_TAG_TYPE_NEW } from 'services/node/utils'
import {
  URL_RECENTLY_ADDED,
  URL_YOGA_SERIES,
  URL_PILATES_FITNESS,
  URL_MEDITATION_PRACTICES,
} from 'services/url/constants'
import { getYogaFitnessLevel } from 'services/tile'
import { getLabel } from 'services/lang'

export const CONTENT_IMAGE_TYPE_KEYART_WITH_TEXT = 'd6-keyart-with-text'
export const CONTENT_IMAGE_TYPE_KEYART_NO_TEXT = 'd6-keyart-no-text'
export const CONTENT_IMAGE_TYPE_KEYART_4K_WITH_TEXT = 'keyart-4k-with-text'
export const CONTENT_IMAGE_TYPE_KEYART_4K_NO_TEXT = 'keyart-4k-no-text'
export const CONTENT_IMAGE_TYPE_HERO_NO_TEXT = 'd6-hero-no-text'
export const CONTENT_IMAGE_TYPE_HERO_WITH_TEXT = 'd6-hero-with-text'
export const CONTENT_IMAGE_TYPE_POSTER = 'd6-poster-image'
export const CONTENT_IMAGE_TYPE_POSTER_3X4 = 'd6-poster-3x4'
export const CONTENT_IMAGE_TYPE_HEADSHOT = 'headshot-image'
export const CONTENT_IMAGE_TYPE_TERM = 'term'
export const CONTENT_IMAGE_TYPE_VISUAL_INTEREST = 'visual-interest'
export const CONTENT_IMAGE_TYPE_LOGO = 'd6-logo'

export const CONTENT_IMAGE_LIST = [
  CONTENT_IMAGE_TYPE_KEYART_WITH_TEXT,
  CONTENT_IMAGE_TYPE_KEYART_NO_TEXT,
  CONTENT_IMAGE_TYPE_KEYART_4K_WITH_TEXT,
  CONTENT_IMAGE_TYPE_KEYART_4K_NO_TEXT,
  CONTENT_IMAGE_TYPE_HERO_NO_TEXT,
  CONTENT_IMAGE_TYPE_POSTER,
  CONTENT_IMAGE_TYPE_POSTER_3X4,
  CONTENT_IMAGE_TYPE_HEADSHOT,
  CONTENT_IMAGE_TYPE_VISUAL_INTEREST,
  CONTENT_IMAGE_TYPE_LOGO,
]

export const PERSON_IMAGE_LIST = [
  CONTENT_IMAGE_TYPE_HEADSHOT,
  CONTENT_IMAGE_TYPE_TERM,
]

export const CONTENT_MEDIA_REFERENCE_TYPE_MEDIA_STREAM = 'ms'
export const CONTENT_MEDIA_REFERENCE_TYPE_CLOSED_CAPTION = 'cc'
export const CONTENT_MEDIA_REFERENCE_TYPE_SUBTITLE = 'st'

export const CONTENT_TAG_GENRE_INTERVIEW_ID = 26649

/**
 * Formatter for content data
 * @param {Map} data - The data
 * @returns {Map} The formatted content data
 */
export function getFormattedContentData (data = Map()) {
  if (!Map.isMap(data) || !data.size) {
    return Map()
  }

  const contentData = data.withMutations((content) => {
    const contentFeatured = Map.isMap(content.get('contentFeatured')) ? content.get('contentFeatured', Map()) : Map()
    const contentFeaturedType = contentFeatured.get('featuredType')
    const contentFeaturedContent = Map.isMap(contentFeatured.get('content')) ? contentFeatured.get('content', Map()) : Map()
    const contentSeries = List.isList(content.get('contentSeries')) ? content.get('contentSeries', List()) : List()
    let featuredContent = null

    if (contentFeatured.size && contentFeaturedContent.size) {
      featuredContent = contentFeaturedContent.withMutations((featured) => {
        return featured
          .set('featuredType', contentFeaturedType)
          .set('vote', formatVote(contentFeaturedContent.get('contentAnalytic')))
          .set('references', formatReferences(contentFeaturedContent.get('references')))
          .set('contentSubtype', contentFeaturedContent.get('subType'))
          .set('assets', formatAssets(contentFeaturedContent.get('contentAssetReferences', null)))
          .set('products', formatProducts(contentFeaturedContent.get('contentReferencesFrom')))
          // remove unneeded data
          .delete('contentAnalytic')
          .delete('subType')
          .delete('contentAssetReferences')
          .delete('contentReferencesFrom')
      })
    }

    content
      .set('vote', formatVote(content.get('contentAnalytic')))
      .set('images', formatImages(content.get('imageContentRelationships')))
      .set('references', formatReferences(content.get('references')))
      .set('featuredContent', featuredContent)
      .set('contentSubtype', content.get('subType'))
      .set('assets', formatAssets(content.get('contentAssetReferences', null)))
      .set('products', formatProducts(content.get('contentReferencesFrom', null)))
      .set('seriesId', contentSeries.getIn([0, 'contentId'], null))
      .set('seriesTitle', contentSeries.getIn([0, 'title'], null))
      .set('seriesPath', contentSeries.getIn([0, 'path'], null))
      .set('seriesStudio', contentSeries.getIn([0, 'studio'], null))
      .set('seriesImages', formatImages(contentSeries.getIn([0, 'imageContentRelationships'], null)))
      .set('seriesShortDescription', contentSeries.getIn([0, 'shortDescription'], null))
      .set('seriesDescription', contentSeries.getIn([0, 'description'], null))
      .set('seriesEpisodesCount', contentSeries.getIn([0, 'episodesCount'], null))
      .set('seriesSeasonsCount', contentSeries.getIn([0, 'seasonsCount'], null))
      .set('seriesContentType', contentSeries.getIn([0, 'contentType'], null))
      .set('seriesContentSubtype', contentSeries.getIn([0, 'subType'], null))
      .set('seriesDisplayType', contentSeries.getIn([0, 'displayType'], null))
      .set('seriesFeaturedType', contentSeries.getIn([0, 'contentFeatured', 'featuredType'], null))
      .set('seriesPreview', contentSeries.getIn([0, 'previewMedia'], null))
      .set('seriesInstructor', contentSeries.getIn([0, 'instructor'], null))
      .set('seriesCast', contentSeries.getIn([0, 'cast'], null))
      .set('seriesVote', formatVote(contentSeries.getIn([0, 'contentAnalytic'])))
      .set('seriesAssets', formatAssets(contentSeries.getIn([0, 'contentAssetReferences'], null)))
      .set('seriesProducts', formatProducts(contentSeries.getIn([0, 'contentReferencesFrom'], null)))
      // remove unneeded data
      .delete('contentAnalytic')
      .delete('imageContentRelationships')
      .delete('contentAssetReferences')
      .delete('contentFeatured')
      .delete('subType')
      .delete('contentSeries')
      .delete('contentReferencesFrom')
  })

  return contentData
}

/**
 * Formatter for episode data
 * @param {Map} data - The episode data
 * @returns {Map} The formatted episode data
 */
export function getFormattedEpisodeData (data = Map()) {
  if (!Map.isMap(data) || !data.size) {
    return Map()
  }

  const episodesList = data.get('contentEpisodes', List()) || List()
  const allEpisodesList = data.get('allContentEpisodes', List()) || List()
  const seasonNumbers = allEpisodesList.map((e) => e.get('seasonNumber')) || List()
  // get a unique list of season numbers using Set()
  const seasonNumbersList = Set(seasonNumbers).toList()
  const episodes = episodesList.map((e) => e.withMutations((episode) => {
    const contentSeries = List.isList(episode.get('contentSeries')) ? episode.get('contentSeries', List()) : List()
    episode
      .set('images', formatImages(episode.get('imageContentRelationships')))
      .set('contentSubtype', episode.get('subType'))
      .set('references', formatReferences(episode.get('references')))
      .set('seriesId', contentSeries.getIn([0, 'contentId'], null))
      .set('seriesTitle', contentSeries.getIn([0, 'title'], null))
      .delete('imageContentRelationships')
      .delete('contentAssetReferences')
      .delete('subType')
      .delete('contentSeries')
  }))

  return Map({
    episodes,
    seasonNumbers: seasonNumbersList,
  })
}

/**
 * Formatter for vote data
 * @param {Map} data - The vote data
 * @returns {Map} The formatted vote data
 */
export function formatVote (data = Map()) {
  if (!Map.isMap(data) || !data.size) {
    return Map({ upVote: 0, downVote: 0 })
  }

  const votes = Map().withMutations((vote) => {
    vote
      .set('upVote', data.get('votesUpCountTotal', 0))
      // create the down vote value
      .set('downVote', data.get('votesCountTotal', 0) - data.get('votesUpCountTotal', 0))
  })

  return votes
}

/**
 * Formatter for image data
 * @param {List} data - The image data
 * @returns {List} The formatted image data
 */
export function formatImages (data = List()) {
  if (!List.isList(data) || !data.size) {
    return List()
  }

  const formattedImages = data.reduce((acc, image) => {
    return acc.push(Map({ type: image.get('type'), url: image.getIn(['image', 'url']) }))
  }, List())

  return formattedImages
}

/**
 * Formatter for asset data
 * @param {List} assets - The asset data
 * @returns {List} The formatted asset data
 */
export function formatAssets (assets = List()) {
  if (!List.isList(assets) || !assets.size) {
    return List()
  }

  const formattedAssets = assets.reduce((acc, asset) => {
    return acc.push(Map({
      type: asset.getIn(['contentAssetReferenceType', 'type']),
      url: asset.getIn(['asset', 'renderUrl']),
      language: asset.getIn(['asset', 'language']),
      fileName: asset.getIn(['asset', 'originalFileName']),
      uuid: asset.getIn(['asset', 'uuid']),
    }))
  }, List())

  return formattedAssets
}

/**
 * Formatter for product data
 * @param {List} products - The products data
 * @returns {List} The formatted products data
 */
export function formatProducts (products = List()) {
  if (!List.isList(products) || !products.size) {
    return List()
  }
  const productList = products.map((p) => {
    const productData = p.getIn(['contentTo', 0])
    if (!Map.isMap(productData) || !productData.size) {
      return Map()
    }

    return fromJS({
      contentType: productData.get('contentType'),
      contentId: productData.get('contentId'),
      ctaProductUrl: productData.get('ctaProductUrl'),
      title: productData.get('title'),
      images: formatImages(productData.get('imageContentRelationships')),
    })
  })

  // remove any items that are empty (no contentId)
  return productList.filter((i) => i.get('contentId'))
}

/**
 * Formatter for references data
 * @param {List} data - The references data
 * @returns {List} The formatted references data
 */
export function formatReferences (data = List()) {
  if (!List.isList(data) || !data.size) {
    return List()
  }

  const formattedReferences = data.reduce((acc, reference) => {
    const item = Map().withMutations((ref) => {
      ref
        .set('type', reference.getIn(['contentReferenceType', 'type']))
        .set('contentId', reference.getIn(['contentTo', 0, 'contentId']))
        .set('contentType', reference.getIn(['contentTo', 0, 'contentType']))
        .set('language', reference.getIn(['contentTo', 0, 'language']))
        .set('path', reference.getIn(['contentTo', 0, 'path']))
        .set('title', reference.getIn(['contentTo', 0, 'title']))
    })

    return acc.push(item)
  }, List())

  return formattedReferences
}

/**
 * Order a content array using an ordering array
 * @param {Number[]} orderArray - The array of ids to order the content by
 * @param {{contentId: Number, contentType: String}[]} contentArray - The array of content
 * @param {String} key - The key to match on
 * @returns The ordered content array
 */
export function getOrderedContent (orderArray = [], contentArray = [], key = '') {
  return _reduce(orderArray, (list, id) => {
    const item = _find(contentArray, (i) => _get(i, key) === id)

    // if we can't find the item, just return the accumulator
    if (!item) {
      return list
    }

    list.push(item)

    return list
  }, [])
}

/**
 * Format a titleGroupId
 * @param {String} contentId - The content id
 * @param {String} contentType - The content type
 * @returns {String} The formatted titleGroupId
 */
export function createTitleGroupId (contentId, contentType) {
  if (!contentId || !contentType) {
    return ''
  }

  return `${contentId}-${contentType}`
}

/**
 * Check to see if the feature is a Gaia+ feature
 * @param {Map} feature - The featured media
 * @returns {Boolean} Whether or not the feature is a Gaia+ feature
 */
export function isGaiaPlusFeature (feature) {
  if (!Map.isMap(feature)) {
    return false
  }

  const allowedSubscriptions = feature.getIn(['contentPolicy', 'optionIds'], List()) || List()

  if (!allowedSubscriptions.size) {
    return false
  }

  return allowedSubscriptions.size === 1
    && allowedSubscriptions.includes(SUBSCRIPTION_CODE_EVENTS_PLUS)
}

/**
 * Determine whether or not the user should see the Upgrade CTA for Gaia+
 * @param {Object} options - The options
 * @param {Map} options.feature - The featured media
 * @param {List} options.subscriptions - The user's subscriptions (entitlements)
 * @param {Boolean} options.isFeatureAllowed - Whether or not the feature is allowed
 *  with the user's current subscription level
 * @returns {Boolean} Whether or not the user should see the upgrade to watch CTA for Gaia+
 */
export function upgradeToWatchCheck (options) {
  const {
    feature,
    subscriptions,
    isFeatureAllowed,
    isFreeAllowed,
  } = options

  // if it is free, no need to upgrade
  if (isFreeAllowed) {
    return false
  }

  return isGaiaPlusFeature(feature) && !isFeatureAllowed && !hasLiveAccessEntitlement(subscriptions)
}

export function featureAllowedWithUserSubscription (props) {
  const featureMedia = getFeatureMedia(props)
  const allowed = featureMedia.getIn(['contentPolicy', 'accessCheck', 'subscriptionsAllowed'])
    || featureMedia.getIn(['contentPolicy', 'accessCheck', 'isFreeAllowed'])

  return allowed
}

/**
 * An list of tags
 * @param {Object} props - The props
 * @param {Map} props.url - The content url
 * @param {Boolean} props.isNew - Whether or not the content is new (published in the last 7 days)
 * @param {List} props.tags - The list of content tags (references)
 * @param {Map} props.staticText - The static text for the tile.v2 component
 * @param {String} props.contentType - The type of content
 * @param {String} props.userLanguage - The primary language of the user
 * @param {String} props.contentSubtype - The content subtype of the content
 * @param {Boolean} props.hasNewEpisodes - Whether or not the content has new episodes
 * @returns {List} An immutable list of tags
 */
export function getContentTags (props) {
  const {
    url,
    isNew,
    tags,
    staticText,
    contentType,
    userLanguage,
    contentSubtype,
    hasNewEpisodes,
    displayType,
  } = props
  const yogaStyleTag = tags.find((t) => (t.get('reference_type') === TYPE_TAG_YOGA_STYLE || t.get('type') === TYPE_TAG_YOGA_STYLE) && t.get('language') === userLanguage)
  const yogaLevelTag = tags.find((t) => (t.get('reference_type') === TYPE_TAG_YOGA_LEVEL || t.get('type') === TYPE_TAG_YOGA_LEVEL) && t.get('language') === userLanguage)
  const yogaDurationTag = tags.find((t) => (t.get('reference_type') === TYPE_TAG_YOGA_DURATION || t.get('type') === TYPE_TAG_YOGA_DURATION) && t.get('language') === userLanguage)
  const yogaFocusTag = tags.find((t) => (t.get('reference_type') === TYPE_TAG_YOGA_FOCUS || t.get('type') === TYPE_TAG_YOGA_FOCUS) && t.get('language') === userLanguage)
  const fitnessStyleTag = tags.find((t) => (t.get('reference_type') === TYPE_TAG_FITNESS_STYLE || t.get('type') === TYPE_TAG_FITNESS_STYLE) && t.get('language') === userLanguage)
  const fitnessLevelTag = tags.find((t) => (t.get('reference_type') === TYPE_TAG_FITNESS_LEVEL || t.get('type') === TYPE_TAG_FITNESS_LEVEL) && t.get('language') === userLanguage)
  const fitnessDurationTag = tags.find((t) => (t.get('reference_type') === TYPE_TAG_FITNESS_DURATION || t.get('type') === TYPE_TAG_FITNESS_DURATION) && t.get('language') === userLanguage)
  const fitnessFocusTag = tags.find((t) => (t.get('reference_type') === TYPE_TAG_FITNESS_FOCUS || t.get('type') === TYPE_TAG_FITNESS_FOCUS) && t.get('language') === userLanguage)
  const meditationStyleTag = tags.find((t) => (t.get('reference_type') === TYPE_TAG_MEDITATION_STYLE || t.get('type') === TYPE_TAG_MEDITATION_STYLE) && t.get('language') === userLanguage)
  const meditationLevelTag = tags.find((t) => (t.get('reference_type') === TYPE_TAG_MEDITATION_LEVEL || t.get('type') === TYPE_TAG_MEDITATION_LEVEL) && t.get('language') === userLanguage)
  const meditationDurationTag = tags.find((t) => (t.get('reference_type') === TYPE_TAG_MEDITATION_DURATION || t.get('type') === TYPE_TAG_MEDITATION_DURATION) && t.get('language') === userLanguage)
  const meditationFocusTag = tags.find((t) => (t.get('reference_type') === TYPE_TAG_MEDITATION_FOCUS || t.get('type') === TYPE_TAG_MEDITATION_FOCUS) && t.get('language') === userLanguage)
  const genreTags = tags.filter((t) => (t.get('reference_type') === TYPE_TAG_GENRE || t.get('type') === TYPE_TAG_GENRE) && t.get('language') === userLanguage) || List()
  const centerTags = tags.filter((t) => (t.get('reference_type') === TYPE_TAG_CENTERS || t.get('type') === TYPE_TAG_CENTERS) && t.get('language') === userLanguage) || List()
  let centersGenres = List()
  const derivedTags = List().withMutations((mutatedDerivedTags) => {
    // both videos and series get "new"
    if (isNew) {
      mutatedDerivedTags.push(Map({
        name: staticText.get('new'),
        path: URL_RECENTLY_ADDED,
        type: NODE_TAG_TYPE_NEW,
      }))
    }

    // if this is a series and it has new episodes
    if (hasNewEpisodes && contentType === TYPE_CONTENT_SERIES) {
      switch (contentSubtype) {
        case SUBTYPE_CONTENT_YOGA:
        case SUBTYPE_CONTENT_MEDITATION:
          mutatedDerivedTags.push(Map({
            name: staticText.get('newPractice'),
            path: url,
          }))
          break
        case SUBTYPE_CONTENT_FITNESS:
          mutatedDerivedTags.push(Map({
            name: staticText.get('newWorkout'),
            path: url,
          }))
          break
        default:
          mutatedDerivedTags.push(Map({
            name: staticText.get('newEpisode'),
            path: url,
          }))
          break
      }
    }

    // tags for series
    if (contentType === TYPE_CONTENT_SERIES) {
      switch (contentSubtype) {
        case SUBTYPE_CONTENT_YOGA:
          mutatedDerivedTags.push(Map({
            name: staticText.get('yogaSeries'),
            path: URL_YOGA_SERIES,
          }))
          break
        case SUBTYPE_CONTENT_FITNESS:
          mutatedDerivedTags.push(Map({
            name: staticText.get('fitnessSeries'),
            path: URL_PILATES_FITNESS,
          }))
          break
        case SUBTYPE_CONTENT_MEDITATION:
          if (displayType !== TYPE_DISPLAY_GUIDE) {
            mutatedDerivedTags.push(Map({
              name: staticText.get('meditationProgram'),
              path: URL_MEDITATION_PRACTICES,
            }))
          } else {
            // for Guides, just show the Genre
            centersGenres = centersGenres.concat(genreTags)
          }
          break
        case SUBTYPE_CONTENT_EVENT:
          centersGenres = centersGenres.concat(genreTags)
          break
        default:
          centersGenres = centersGenres.concat(genreTags, centerTags)
          break
      }
    }

    // tags for videos/episodes
    if (contentType === TYPE_CONTENT_VIDEO) {
      switch (contentSubtype) {
        case SUBTYPE_CONTENT_YOGA: {
          if (yogaStyleTag) {
            mutatedDerivedTags.push(yogaStyleTag)
          }

          if (yogaLevelTag) {
            const updatedYogaLevelTag = yogaLevelTag
              .withMutations((mutatedYogaLevelTag) => {
                return mutatedYogaLevelTag.set('title', getYogaFitnessLevel(yogaLevelTag.get('title')))
              })
            mutatedDerivedTags.push(updatedYogaLevelTag)
          }

          if (yogaDurationTag) {
            mutatedDerivedTags.push(yogaDurationTag)
          }

          if (yogaFocusTag) {
            mutatedDerivedTags.push(yogaFocusTag)
          }
          break
        }
        case SUBTYPE_CONTENT_FITNESS:
          if (fitnessStyleTag) {
            mutatedDerivedTags.push(fitnessStyleTag)
          }

          if (fitnessLevelTag) {
            const updatedFitnessLevelTag = fitnessLevelTag
              .withMutations((mutatedFitnessLevelTag) => {
                return mutatedFitnessLevelTag.set('title', getYogaFitnessLevel(fitnessLevelTag.get('title')))
              })
            mutatedDerivedTags.push(updatedFitnessLevelTag)
          }

          if (fitnessDurationTag) {
            mutatedDerivedTags.push(fitnessDurationTag)
          }

          if (fitnessFocusTag) {
            mutatedDerivedTags.push(fitnessFocusTag)
          }
          break
        case SUBTYPE_CONTENT_MEDITATION:
          mutatedDerivedTags.push(Map({
            name: staticText.get('meditation'),
            path: URL_MEDITATION_PRACTICES,
          }))
          if (meditationStyleTag) {
            mutatedDerivedTags.push(meditationStyleTag)
          }

          if (meditationLevelTag) {
            const updatedMeditationLevelTag = meditationLevelTag
              .withMutations((mutatedMeditationLevelTag) => {
                return mutatedMeditationLevelTag.set('title', getYogaFitnessLevel(meditationLevelTag.get('title')))
              })
            mutatedDerivedTags.push(updatedMeditationLevelTag)
          }

          if (meditationDurationTag) {
            mutatedDerivedTags.push(meditationDurationTag)
          }

          if (meditationFocusTag) {
            mutatedDerivedTags.push(meditationFocusTag)
          }
          break
        case SUBTYPE_CONTENT_EVENT:
          centersGenres = centersGenres.concat(genreTags)
          break
        default:
          centersGenres = centersGenres.concat(genreTags, centerTags)
          break
      }
    }

    return mutatedDerivedTags
  })

  // return the top 4 tags
  return derivedTags.concat(centersGenres).slice(0, 4)
}

/**
 * Whether or not the Interview genre is in the tags list
 * @param {Object} props - The props
 * @param {List} props.tags - List of references/tags
 * @returns {Boolean} - Whether or not the Interview genre is in the tags list
 */
export function isGenreInterview (props) {
  const { tags } = props
  const genreInterview = tags.find((t) => (t.get('content_id') === _toString(CONTENT_TAG_GENRE_INTERVIEW_ID) || t.get('contentId') === CONTENT_TAG_GENRE_INTERVIEW_ID)) || Map()

  return !!genreInterview.size
}

/**
 * Get the feature media from GraphQL data
 * @param {Object} props - The props
 * @param {String} props.contentType - The type of content
 * @param {Map} props.data - The content data
 * @returns {Map} The feature media
 */
export function getFeatureMedia (props) {
  const { data, contentType } = props
  let featureMedia = Map.isMap(data.get('featureMedia')) ? data.get('featureMedia', Map()) : Map()

  if (contentType === TYPE_CONTENT_SERIES) {
    featureMedia = Map.isMap(data.getIn(['featuredContent', 'featureMedia'])) ? data.getIn(['featuredContent', 'featureMedia'], Map()) : Map()
  }

  return featureMedia
}

/**
 * Get the feature content from GraphQL data
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @returns {Map} The feature content
 */
export function getFeaturedContent (props) {
  const { data } = props
  const featuredContent = Map.isMap(data.get('featuredContent')) ? data.get('featuredContent', Map()) : Map()

  return featuredContent
}

/**
 * Get the base url for a playable piece of content (graphql)
 * @param {Object} props - The props
 * @param {String} props.contentType - The type of content
 * @param {Map} props.data - The content data
 * @returns {Map} The base url for a playable piece of content
 */
export function getPlayableUrl (props) {
  const { contentType, data } = props
  let url = data.get('path')

  if (contentType === TYPE_CONTENT_SERIES) {
    const featuredContent = getFeaturedContent(props)
    url = featuredContent.get('path', '')
  }

  return url
}

/**
 * Determine if the content is a Video
 * @param {Object} props - The props
 * @param {String} props.contentType - The type of content
 * @param {Map} props.data - The content data
 * @returns {Boolean} if the content is a video, true/false
 */
export function isVideo (props) {
  const { contentType, data = Map() } = props
  const seriesId = data.get('seriesId', 0)

  // Check if contentType is 'video' and there is no seriesId in the data
  if (contentType === TYPE_CONTENT_VIDEO && (!seriesId || seriesId <= 0)) {
    return true
  }

  return false
}

/**
 * Determine if the content is an episode
 * @param {Object} props - The props
 * @param {String} props.contentType - The type of content
 * @param {Map} props.data - The content data
 * @returns {Boolean} if the content is an episode, true/false
 */
export function isEpisode (props) {
  const { contentType, data = Map() } = props
  const seriesId = data.get('seriesId', 0)
  const dataContentType = data.get('contentType')
  const type = contentType || dataContentType

  if (type === TYPE_CONTENT_VIDEO && seriesId && seriesId > 0) {
    return true
  }

  return false
}

/**
 * Determine if the content is a Series
 * @param {Object} props - The props
 * @param {String} props.contentType - The type of content
 * @returns {Boolean} if the content is a series, true/false
 */
export function isSeries (props) {
  const { contentType } = props

  return contentType === TYPE_CONTENT_SERIES
}

/**
 * Determine if the contentSubtype is an Event
 * @param {Object} props - The props
 * @param {String} props.contentSubtype - The subtype of content
 * @returns {Boolean} if the content is an event, true/false
 */
export function isSubtypeEvent (props) {
  const { contentSubtype } = props

  return contentSubtype === SUBTYPE_CONTENT_EVENT
}

/**
 * Determine if the contentSubtype is an Yoga, Fitness or Meditation
 * @param {Object} props - The props
 * @param {String} props.contentSubtype - The subtype of content
 * @returns {Boolean} if the content is yoga, fitness or meditation, true/false
 */
export function isSubtypeYogaFitnessMeditation (props) {
  const { contentSubtype } = props

  return contentSubtype === SUBTYPE_CONTENT_YOGA
    || contentSubtype === SUBTYPE_CONTENT_FITNESS
    || contentSubtype === SUBTYPE_CONTENT_MEDITATION
}

/**
 * Determine if the content is a Guide
 * @param {Object} props - The props
 * @param {String} props.contentSubtype - The subtype of content
 * @param {String}  props.displayType - The display type
 * @returns {Boolean} if the content is an guide, true/false
 */
export function isGuide (props) {
  const { contentSubtype, displayType } = props

  return contentSubtype === SUBTYPE_CONTENT_GUIDE || displayType === TYPE_DISPLAY_GUIDE
}

/**
 * Determine if the content is a series with no episodes
 * @param {Object} props - The props
 * @param {String} props.contentSubtype - The subtype of content
 * @param {Map}  props.data - The content data
 * @returns {Boolean} if the content is a series with no episodes, true/false
 */
export function isSeriesNoEpisodes (props) {
  const { contentType, data = Map() } = props

  if (isSeries({ contentType })) {
    const totalEpisodes = data.get('episodesCount')
    return !totalEpisodes || totalEpisodes === 0 || totalEpisodes === '0'
  }

  return false
}

/**
 * Determine if the content is a Guide
 * @param {Object} props - The props
 * @param {String} props.contentSubtype - The subtype of content
 * @param {String}  props.displayType - The display type
 * @param {Map} props.data - The data
 * @returns {Boolean} if the content is an guide and has not been started yet, true/false
 */
export function isGuideNotStartedYet (props) {
  const { contentSubtype, displayType, data = Map() } = props

  return isGuide({ contentSubtype, displayType }) && data.getIn(['featuredContent', 'featuredType']) === 'first'
}

/**
 * Get the available audio languages for a feature
 * @param {Object} props - The props
 * @param {String} props.contentSubtype - The subtype of content
 * @param {Map} props.data - The data
 * @returns {String} A string of comma separated languages
 */
export function getAudioLanguages (props) {
  const { data, contentType } = props
  const feature = getFeatureMedia({ data, contentType })
  const mediaAssets = List.isList(feature.get('mediaAssets')) ? feature.get('mediaAssets', List()) : List()
  const mediaStreams = mediaAssets.filter((a = Map()) => {
    return a.get('mediaReferenceType') === CONTENT_MEDIA_REFERENCE_TYPE_MEDIA_STREAM
  }) || List()
  const availableLangs = mediaStreams.map((i) => {
    return i.get('language')
  }, List())
  const availableLanguages = availableLangs.map((i) => {
    return getLabel(i)
  })

  return availableLanguages.join(', ')
}

/**
 * Get the available subtitle and caption languages for a feature
 * @param {Object} props - The props
 * @param {String} props.contentSubtype - The subtype of content
 * @param {Map} props.data - The data
 * @returns {String} A string of comma separated languages
 */
export function getSubtitleCaptionLanguages (props) {
  const { data, contentType } = props
  const feature = getFeatureMedia({ data, contentType })
  const mediaAssets = List.isList(feature.get('mediaAssets')) ? feature.get('mediaAssets', List()) : List()
  const subtitles = mediaAssets.filter((a = Map()) => {
    return a.get('mediaReferenceType') === CONTENT_MEDIA_REFERENCE_TYPE_SUBTITLE
  }) || List()
  const captions = mediaAssets.filter((a = Map()) => {
    return a.get('mediaReferenceType') === CONTENT_MEDIA_REFERENCE_TYPE_CLOSED_CAPTION
  }) || List()
  const availableSubtitles = subtitles.map((i) => {
    return i.get('language')
  }, List())
  const availableCaptions = captions.map((i) => {
    return i.get('language')
  }, List())
  const availableSubtitlesCaptions = Set().union(
    availableSubtitles,
    availableCaptions,
  )
  const subtitleLanguages = availableSubtitlesCaptions.map((i) => {
    return getLabel(i)
  })

  return subtitleLanguages.join(', ')
}

/**
 * Get the location type (type of page)
 * @param {Object} props - The props
 * @param {Map} props.data - The data
 * @returns {String} The location type (video, series, event, etc.)
 */
export function getLocationType (props) {
  const { data } = props
  const contentType = data.get('contentType')
  const contentSubtype = data.get('contentSubtype')
  const displayType = data.get('displayType')
  let detailLocationType = TYPE_CONTENT_VIDEO

  if (isSeries({ contentType })) {
    detailLocationType = TYPE_CONTENT_SERIES
  }

  if (isEpisode({ data, contentType })) {
    detailLocationType = TYPE_CONTENT_EPISODE
  }

  if (isSubtypeEvent({ contentSubtype })) {
    detailLocationType = SUBTYPE_CONTENT_EVENT
  }

  if (isGuide({ contentSubtype, displayType })) {
    detailLocationType = SUBTYPE_CONTENT_GUIDE
  }

  return detailLocationType
}

/**
 * Format / map GraphQL content data to the old node tile data shape
 * @param {Object} contentData - The GraphQL content data
 * @returns {Object} - Content data in the old node shape for tiles, spotlight, etc.
 */
export function formatDataToLegacy (contentData) {
  const {
    published,
    previewMedia,
    featureMedia,
    contentId,
    contentSeries,
    subType,
    contentType,
    title,
    languageFullText,
    shortDescription,
    seasonNumber,
    episodeNumber,
    seasonsCount,
    episodesCount,
    path,
    instructor,
    references,
    createdAt,
    imageContentRelationships,
    ctaLabel,
    ctaPath,
    cast,
  } = contentData
  const yogaStyleReference = _find(references, (r) => _get(r, ['contentReferenceType', 'type']) === TYPE_TAG_YOGA_STYLE) || {}
  const yogaLevelReference = _find(references, (r) => _get(r, ['contentReferenceType', 'type']) === TYPE_TAG_YOGA_LEVEL) || {}
  const yogaStyle = _get(yogaStyleReference, ['contentTo', 0, 'title'])
  const yogaLevel = _get(yogaLevelReference, ['contentTo', 0, 'title'])
  const fitnessStyleReference = _find(references, (r) => _get(r, ['contentReferenceType', 'type']) === TYPE_TAG_FITNESS_STYLE) || {}
  const fitnessLevelReference = _find(references, (r) => _get(r, ['contentReferenceType', 'type']) === TYPE_TAG_FITNESS_LEVEL) || {}
  const fitnessStyle = _get(fitnessStyleReference, ['contentTo', 0, 'title'])
  const fitnessLevel = _get(fitnessLevelReference, ['contentTo', 0, 'title'])
  const meditationStyleReference = _find(references, (r) => _get(r, ['contentReferenceType', 'type']) === TYPE_TAG_MEDITATION_STYLE) || {}
  const meditationStyle = _get(meditationStyleReference, ['contentTo', 0, 'title'])
  const heroImageNoText = _find(imageContentRelationships, (i) => _get(i, 'type') === CONTENT_IMAGE_TYPE_HERO_NO_TEXT) || {}
  const heroImage = _get(heroImageNoText, ['image', 'url'], '')
  const keyArtImageWithText = _find(imageContentRelationships, (i) => _get(i, 'type') === CONTENT_IMAGE_TYPE_KEYART_WITH_TEXT) || {}
  const keyArtImage4kWithText = _find(imageContentRelationships, (i) => _get(i, 'type') === CONTENT_IMAGE_TYPE_KEYART_4K_WITH_TEXT) || {}
  const largeKeyArtImage = _get(keyArtImage4kWithText, ['image', 'url'], '') || _get(keyArtImageWithText, ['image', 'url'], '') || ''
  const visualInterestImage = _find(imageContentRelationships, (i) => _get(i, 'type') === CONTENT_IMAGE_TYPE_VISUAL_INTEREST) || {}
  const visualInterestXl = _get(visualInterestImage, ['image', 'url'], '')
  const contentLogoImage = _find(imageContentRelationships, (i) => _get(i, 'type') === CONTENT_IMAGE_TYPE_LOGO) || {}
  const logoImage = _get(contentLogoImage, ['image', 'url'], '')
  const formattedData = {
    published,
    created: createdAt,
    id: contentId,
    contentType,
    contentSubtype: subType,
    title,
    url: path,
    teaser: shortDescription,
    duration: _get(featureMedia, 'duration', 0),
    preview: {
      id: _toNumber(_get(previewMedia, 'mediaId', 0) || 0),
    },
    feature: {
      id: _toNumber(_get(featureMedia, 'mediaId', 0) || 0),
      accessAllowed: _get(featureMedia, ['contentPolicy', 'accessCheck', 'allowed'], true),
    },
    seriesTitle: _get(contentSeries, [0, 'title'], ''),
    seriesPath: _get(contentSeries, [0, 'path'], ''),
    seriesId: _get(contentSeries, [0, 'contentId'], -1),
    season: seasonNumber || null,
    episode: episodeNumber || null,
    seasonCount: seasonsCount,
    episodeCount: episodesCount,
    host: instructor,
    guest: cast,
    mediaLanguage: languageFullText,
    yogaStyle,
    yogaLevel,
    fitnessStyle,
    fitnessLevel,
    meditationStyle,
    largeKeyArtImage,
    heroImage: {
      small: heroImage,
      mediumSmall: heroImage,
      medium: heroImage,
      large: heroImage,
    },
    visual_interest_image: {
      visual_interest_xl: visualInterestXl,
    },
    logo_image: {
      logo_xl: logoImage,
    },
    cta: {
      path: ctaPath,
      label: ctaLabel,
    },
  }

  return formattedData
}
